import { apiService } from "../api";
import { ModulePermissionProfile } from "../module-permissions";
import { BaseApiResponse } from "../program-dashboard";
import { assertRolePolicyV2Enabled } from "@/config/feature-flags";

/**
 * Get all non-customer roles for a given program
 */
interface ChildPortfolio {
  _id: string;
  name: string;
  id: string;
}

export interface RoleProfile {
  role_id: string;
  name: string;
  abbreviation: string;
  description: string;
  portfolio_relationship: number;
  child_portfolios: ChildPortfolio[];
  role_icon: string;
  program: ChildPortfolio;
  delete_flag: number;
  issues: any[]; // Replace with specific type if issues have a structure
  rank: number;
  lock_flag: number;
  role_type: number;
  type: string;
  module_permission_profile: ModulePermissionProfile[];
  id: string;
  created_at: string;
  updated_at: string;
  isSystemRole?: boolean;
  valid_policy_flag?: 0 | 1;
}

export interface GetProgramRolesResponse extends BaseApiResponse {
  data: RoleProfile[];
}

export type RoleHierarchyMode = "LEGACY" | "POLICY" | "INDETERMINATE";

export function deriveRoleHierarchyMode(roles: Pick<RoleProfile, "valid_policy_flag">[]): RoleHierarchyMode {
  if (roles.length === 0) return "LEGACY";

  const flags = roles.map(role => role.valid_policy_flag);
  const hasInvalidFlag = flags.some(flag => flag !== undefined && flag !== 0 && flag !== 1);
  if (hasInvalidFlag) return "INDETERMINATE";

  const hasPolicyRole = flags.some(flag => flag === 1);
  const hasLegacyRole = flags.some(flag => flag === 0 || flag === undefined);

  if (hasPolicyRole && hasLegacyRole) return "INDETERMINATE";
  return hasPolicyRole ? "POLICY" : "LEGACY";
}

export type RoleAccessMode = "NODE_ONLY" | "NODE_AND_DESCENDANTS";

/**
 * Plain-language labels for the two access modes. Phrased as sentence clauses
 * so they read as the tail of "<role> can see <role> …".
 */
export const ACCESS_MODE_LABELS: Record<RoleAccessMode, string> = {
  NODE_ONLY: "only this role",
  NODE_AND_DESCENDANTS: "and everyone below",
};

export interface RoleReference {
  id: string;
  name: string;
}

export interface RoleAccessPolicy {
  id: string;
  programId: string;
  parent: RoleReference;
  child: RoleReference;
  mode: RoleAccessMode;
}

export interface GetProgramRoleAccessPoliciesResponse extends BaseApiResponse {
  data: RoleAccessPolicy[];
}

export async function getProgramRoles(programId: string): Promise<GetProgramRolesResponse> {
  const response = await apiService({
    url: `/role/program/${programId}`,
    method: "get",
    protectedRoute: true,
  });
  return response;
}

function normalizeReference(value: unknown, fieldName: "parent" | "child"): RoleReference {
  if (typeof value === "string" && value) {
    return { id: value, name: value };
  }

  if (value && typeof value === "object") {
    const reference = value as Record<string, unknown>;
    const id = reference.id ?? reference._id;
    if (typeof id === "string" && id) {
      return {
        id,
        name: typeof reference.name === "string" && reference.name ? reference.name : id,
      };
    }
  }

  throw new Error(`Invalid ${fieldName} role in access policy response`);
}

export function normalizeRoleAccessPolicy(value: unknown): RoleAccessPolicy {
  if (!value || typeof value !== "object") {
    throw new Error("Invalid role access policy response");
  }

  const policy = value as Record<string, unknown>;
  const id = policy.id ?? policy._id;
  const program = policy.program ?? policy.programId;
  let programId: unknown;
  if (typeof program === "string") {
    programId = program;
  } else if (program && typeof program === "object") {
    const programReference = program as Record<string, unknown>;
    programId = programReference.id ?? programReference._id;
  }

  if (typeof id !== "string" || !id || typeof programId !== "string" || !programId) {
    throw new Error("Invalid role access policy identity");
  }

  if (policy.mode !== "NODE_ONLY" && policy.mode !== "NODE_AND_DESCENDANTS") {
    throw new Error("Invalid role access policy mode");
  }

  return {
    id,
    programId,
    parent: normalizeReference(policy.parent, "parent"),
    child: normalizeReference(policy.child, "child"),
    mode: policy.mode,
  };
}

export async function getProgramRoleAccessPolicies(programId: string): Promise<GetProgramRoleAccessPoliciesResponse> {
  assertRolePolicyV2Enabled();
  const response = await apiService({
    url: `/role/v2/access-policy/program/${programId}`,
    method: "get",
    protectedRoute: true,
  });

  if (!Array.isArray(response?.data)) {
    throw new Error("Invalid role access policy list");
  }

  return {
    ...response,
    data: response.data.map(normalizeRoleAccessPolicy),
  };
}

export async function createRoleAccessPolicy(payload: { program: string; parent: string; child: string; mode: RoleAccessMode }) {
  assertRolePolicyV2Enabled();
  return apiService({
    url: "/role/v2/access-policy",
    method: "post",
    protectedRoute: true,
    requestData: payload,
  });
}

export async function deleteRoleAccessPolicy(policyId: string) {
  assertRolePolicyV2Enabled();
  return apiService({
    url: `/role/v2/access-policy/${policyId}`,
    method: "delete",
    protectedRoute: true,
  });
}

/**
 * Get all non-customer roles for a given program
 */
export async function getNonCustomerProgramRoles(programId: string) {
  const response = await apiService({
    url: `/role/program/non-customer-roles/${programId}`,
    method: "get",
    protectedRoute: true,
  });
  return response;
}

/**
 * Get all customer roles for a given program
 */
export async function getProgramCustomerRoles(programId: string) {
  const response = await apiService({
    url: `/role/program/customer-roles/${programId}`,
    method: "get",
    protectedRoute: true,
  });
  return response;
}

/**
 * Create or update a role for a given program
 */
export async function createOrUpdateProgramRole(programId: string, payload: object) {
  const response = await apiService({
    url: `/role/program/${programId}`,
    method: "post",
    protectedRoute: true,
    requestData: payload,
  });
  return response;
}

export async function createProgramRoleV2(programId: string, payload: object) {
  assertRolePolicyV2Enabled();
  return apiService({
    url: `/role/v2/program/${programId}`,
    method: "post",
    protectedRoute: true,
    requestData: payload,
  });
}

/**
 * Update an existing role
 */
export async function updateProgramRole({ programId, roleId, payload }: { programId: string; roleId: string; payload: any }) {
  const response = await apiService({
    url: `/role/program/${programId}/${roleId}`,
    method: "put",
    protectedRoute: true,
    requestData: payload,
  });
  return response;
}

/**
 * Delete a program role
 */
export async function deleteProgramRole(roleId: string) {
  const response = await apiService({
    url: `/role/${roleId}`,
    method: "delete",
    protectedRoute: true,
  });
  return response;
}

/**
 * Get all permission groups
 */
export async function getPermissionGroups() {
  const response = await apiService({
    url: `/role/permission-group`,
    method: "get",
    protectedRoute: true,
  });
  return response;
}

/**
 * Delete a saved draft
 */
export async function deleteSavedDraft(draftId: string) {
  const response = await apiService({
    url: `/role/draft/${draftId}`,
    method: "delete",
    protectedRoute: true,
  });
  return response;
}

/**
 * Get all portfolios
 */
export async function getPortfolios() {
  const response = await apiService({
    url: `/role/portfolio`,
    method: "get",
    protectedRoute: true,
  });
  return response;
}
